import { FC, useEffect, useMemo, useState } from 'react';
import {
  ProjectTransferRequest,
  SelectBase,
  UserListResponse,
  useGetOrganizationsSelectQuery,
  useGetUsersQuery,
  useTransferProjectMutation,
} from '../api/api';
import { Button, CopyableInput, Input, Select } from '../components/Form';

const AUTHOR_ROLES = new Set(['author', 'admin']);

const formatUserFullName = (user: UserListResponse) =>
  `${user.first_name} ${user.last_name}`.trim() || user.email;

const formatUserOptionLabel = (user: UserListResponse) =>
  `${formatUserFullName(user)} - ${user.email} (${user.role_name})`;

const getErrorMessage = (error: unknown) => {
  if (typeof error === 'object' && error !== null) {
    const maybeError = error as {
      data?: { detail?: string };
      error?: string;
    };

    return maybeError.data?.detail || maybeError.error || 'Не удалось перенести сценарий.';
  }

  return 'Не удалось перенести сценарий.';
};

export const ProjectTransfer: FC = () => {
  const [projectId, setProjectId] = useState('');
  const [organizationSearch, setOrganizationSearch] = useState('');
  const [userSearch, setUserSearch] = useState('');
  const [selectedOrganizationId, setSelectedOrganizationId] = useState('');
  const [selectedUserId, setSelectedUserId] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successResult, setSuccessResult] = useState<{
    id: string;
    conversation_id: string;
  } | null>(null);

  const { data: organizations, isLoading: isLoadingOrganizations } = useGetOrganizationsSelectQuery();
  const { data: users, isLoading: isLoadingUsers } = useGetUsersQuery();
  const [transferProject, { isLoading: isTransferring }] = useTransferProjectMutation();

  const organizationOptions = useMemo<SelectBase[]>(() => {
    const normalizedSearch = organizationSearch.trim().toLowerCase();
    const filteredOrganizations = (organizations ?? []).filter((organization) => {
      if (!normalizedSearch) {
        return true;
      }

      return organization.name.toLowerCase().includes(normalizedSearch);
    });

    return filteredOrganizations.sort((left, right) => left.name.localeCompare(right.name));
  }, [organizationSearch, organizations]);

  const eligibleUsers = useMemo<UserListResponse[]>(() => {
    return (users ?? [])
      .filter((user) => AUTHOR_ROLES.has(user.role_name))
      .filter((user) => !selectedOrganizationId || user.organization_id === selectedOrganizationId);
  }, [selectedOrganizationId, users]);

  const userOptions = useMemo<SelectBase[]>(() => {
    const normalizedSearch = userSearch.trim().toLowerCase();

    return eligibleUsers
      .filter((user) => {
        if (!normalizedSearch) {
          return true;
        }

        const fullName = formatUserFullName(user).toLowerCase();
        return (
          fullName.includes(normalizedSearch)
          || user.email.toLowerCase().includes(normalizedSearch)
        );
      })
      .sort((left, right) => formatUserFullName(left).localeCompare(formatUserFullName(right)))
      .map((user) => ({
        _id: user.id,
        name: formatUserOptionLabel(user),
      }));
  }, [eligibleUsers, userSearch]);

  const selectedOrganization = useMemo(
    () => (organizations ?? []).find((organization) => organization._id === selectedOrganizationId),
    [organizations, selectedOrganizationId]
  );

  const selectedUser = useMemo(
    () => eligibleUsers.find((user) => user.id === selectedUserId),
    [eligibleUsers, selectedUserId]
  );

  useEffect(() => {
    if (selectedUserId && !eligibleUsers.some((user) => user.id === selectedUserId)) {
      setSelectedUserId('');
    }
  }, [eligibleUsers, selectedUserId]);

  useEffect(() => {
    setErrorMessage(null);
    setSuccessResult(null);
  }, [projectId, selectedOrganizationId, selectedUserId]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!projectId || !selectedOrganizationId || !selectedUserId) {
      return;
    }

    const requestBody: ProjectTransferRequest = {
      organization_id: selectedOrganizationId,
      user_id: selectedUserId,
    };

    try {
      setErrorMessage(null);
      const response = await transferProject({
        projectId: projectId.trim(),
        body: requestBody,
      }).unwrap();
      setSuccessResult(response);
    } catch (error) {
      setSuccessResult(null);
      setErrorMessage(getErrorMessage(error));
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Project Transfer</h1>
        <p className="mt-2 max-w-3xl text-sm text-gray-600">
          Дублирует сценарий по ID в выбранную организацию и назначает его на выбранного пользователя
          с ролью author или admin. Новый сценарий создаётся как отдельный project с отдельной conversation.
        </p>
      </div>

      <section className="rounded-2xl bg-white p-8 shadow-md">
        <form onSubmit={handleSubmit} className="space-y-6">
          <Input
            label="ID сценария"
            value={projectId}
            onChange={(event) => setProjectId(event.target.value)}
            placeholder="pmxc-vmoe-nzno"
            required
          />

          <div className="grid gap-6 lg:grid-cols-2">
            <div className="rounded-xl border border-gray-200 p-5">
              <Input
                label="Поиск организации"
                value={organizationSearch}
                onChange={(event) => setOrganizationSearch(event.target.value)}
                placeholder="Название организации"
              />
              <Select
                label="Организация"
                value={selectedOrganizationId}
                onChange={(event) => setSelectedOrganizationId(event.target.value)}
                options={organizationOptions}
                disabled={isLoadingOrganizations}
                required
              />
              <p className="text-xs text-gray-500">
                {isLoadingOrganizations
                  ? 'Загружаем организации...'
                  : `Найдено организаций: ${organizationOptions.length}`}
              </p>
            </div>

            <div className="rounded-xl border border-gray-200 p-5">
              <Input
                label="Поиск пользователя"
                value={userSearch}
                onChange={(event) => setUserSearch(event.target.value)}
                placeholder="ФИО или email"
                disabled={!selectedOrganizationId}
              />
              <Select
                label="Пользователь"
                value={selectedUserId}
                onChange={(event) => setSelectedUserId(event.target.value)}
                options={userOptions}
                disabled={!selectedOrganizationId || isLoadingUsers}
                required
              />
              <p className="text-xs text-gray-500">
                {!selectedOrganizationId
                  ? 'Сначала выберите организацию.'
                  : isLoadingUsers
                    ? 'Загружаем пользователей...'
                    : `Найдено пользователей: ${userOptions.length}`}
              </p>
            </div>
          </div>

          {(selectedOrganization || selectedUser) ? (
            <div className="grid gap-4 md:grid-cols-2">
              <div className="rounded-xl bg-slate-50 p-4">
                <div className="text-xs uppercase tracking-wide text-gray-500">Целевая организация</div>
                <div className="mt-2 text-sm font-medium text-gray-900">
                  {selectedOrganization?.name ?? 'Не выбрана'}
                </div>
                {selectedOrganization ? (
                  <div className="mt-1 text-xs text-gray-500">{selectedOrganization._id}</div>
                ) : null}
              </div>

              <div className="rounded-xl bg-slate-50 p-4">
                <div className="text-xs uppercase tracking-wide text-gray-500">Целевой автор</div>
                <div className="mt-2 text-sm font-medium text-gray-900">
                  {selectedUser ? formatUserFullName(selectedUser) : 'Не выбран'}
                </div>
                {selectedUser ? (
                  <div className="mt-1 text-xs text-gray-500">
                    {selectedUser.email} | {selectedUser.role_name} | {selectedUser.id}
                  </div>
                ) : null}
              </div>
            </div>
          ) : null}

          {errorMessage ? (
            <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
              {errorMessage}
            </div>
          ) : null}

          <div className="flex justify-end">
            <Button
              type="submit"
              disabled={isTransferring || !projectId.trim() || !selectedOrganizationId || !selectedUserId}
            >
              {isTransferring ? 'Переносим...' : 'Перенести сценарий'}
            </Button>
          </div>
        </form>
      </section>

      {successResult ? (
        <section className="rounded-2xl border border-emerald-200 bg-emerald-50 p-8">
          <h2 className="text-lg font-semibold text-emerald-900">Сценарий перенесён</h2>
          <p className="mt-2 text-sm text-emerald-800">
            Создан новый сценарий и новая conversation. Идентификаторы ниже.
          </p>
          <div className="mt-6 grid gap-4 md:grid-cols-2">
            <CopyableInput label="Новый project ID" value={successResult.id} />
            <CopyableInput label="Новый conversation ID" value={successResult.conversation_id} />
          </div>
        </section>
      ) : null}
    </div>
  );
};
